/**
 * Sheet-name registry for custom DocHub fields that are not {{tokens}}.
 * Ranges mirror Apps Script print writers in appscript_main_old.js.
 *
 * Sheet matching (same as Apps Script):
 * - Prefer match: 'include' → sheetName.toLowerCase().includes(needle)
 * - Use match: 'exact' only where Apps Script uses === (new_sales_agreement)
 *
 * Shapes:
 * - keyValue → [{ key, value }]  (optional equipment, retailer options, …)
 * - stringList → string[]         (sellerPaidOptions, promiseListItems)
 * - fixedValueList → string[] parallel to app-choice keys (tinyHomeAdditionalProducts)
 * - expandable keyValue → dynamic row insert (21st funds / inspection / commission sheet)
 */

export const OPTIONAL_EQUIPMENT_FIELD = 'optionalEquipmentAndAccessories';
export const RETAILER_OPTIONS_FIELD = 'retailerOptions';
export const SELLER_PAID_OPTIONS_FIELD = 'sellerPaidOptions';
export const PROMISE_LIST_ITEMS_FIELD = 'promiseListItems';
export const TWENTYFIRST_IMPROVEMENT_FUNDS_FIELD = 'twentyfirstImprovementFundsOptions';
export const TWENTIFIRST_IMPROVEMENT_INSPECTION_FIELD = 'twentifirstImprovementInspectionOptions';
export const COUNTRY_PLACE_FHA_PA_IMPROVEMENTS_FIELD = 'countryPlaceFhaPaImprovements';
export const TINY_HOME_ADDITIONAL_PRODUCTS_FIELD = 'tinyHomeAdditionalProducts';
export const COMMISSION_SHEET_OPTIONS_FIELD = 'commissionSheetOptions';
export const COMMISSION_SHEET_LESS_OPTIONS_FIELD = 'commissionSheetLessOptions';

export const KEY_VALUE_TABLE_FIELDS = new Set([
  OPTIONAL_EQUIPMENT_FIELD,
  RETAILER_OPTIONS_FIELD,
  TWENTYFIRST_IMPROVEMENT_FUNDS_FIELD,
  TWENTIFIRST_IMPROVEMENT_INSPECTION_FIELD,
  COUNTRY_PLACE_FHA_PA_IMPROVEMENTS_FIELD,
  COMMISSION_SHEET_OPTIONS_FIELD,
  COMMISSION_SHEET_LESS_OPTIONS_FIELD
]);

/** Stored on inventory; DocHub V2 edits in-sheet then mirrors back for save/print. */
export const INVENTORY_COMMISSION_TABLE_FIELDS = new Set([
  COMMISSION_SHEET_OPTIONS_FIELD,
  COMMISSION_SHEET_LESS_OPTIONS_FIELD
]);

export const STRING_LIST_TABLE_FIELDS = new Set([
  SELLER_PAID_OPTIONS_FIELD,
  PROMISE_LIST_ITEMS_FIELD
]);

/** Fixed app-choice keys; stored as string[] parallel to those keys ('' = unchecked). */
export const FIXED_VALUE_LIST_FIELDS = new Set([TINY_HOME_ADDITIONAL_PRODUCTS_FIELD]);

export const CUSTOM_TABLE_FIELDS = new Set([
  ...KEY_VALUE_TABLE_FIELDS,
  ...STRING_LIST_TABLE_FIELDS,
  ...FIXED_VALUE_LIST_FIELDS
]);

const FIELD_META = {
  [OPTIONAL_EQUIPMENT_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Equipment / accessory',
    valueHint: 'Value',
    appChoiceKey: 'Optional Equipment And Accessories',
    // Mirror create/edit form: a row with a key but no value prints as 'Inc'.
    defaultValue: 'Inc'
  },
  [RETAILER_OPTIONS_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Retailer option',
    valueHint: 'Value',
    appChoiceKey: 'Retailer Options',
    defaultValue: 0
  },
  [SELLER_PAID_OPTIONS_FIELD]: {
    shape: 'stringList',
    textHint: 'Seller paid option',
    appChoiceKey: 'Seller Paid Options'
  },
  [PROMISE_LIST_ITEMS_FIELD]: {
    shape: 'stringList',
    textHint: 'Promise list item',
    appChoiceKey: 'Promise List Items'
  },
  [TWENTYFIRST_IMPROVEMENT_FUNDS_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Description',
    valueHint: 'Amount',
    appChoiceKey: '21st Improvement Funds Options',
    defaultValue: 'Inc'
  },
  [TWENTIFIRST_IMPROVEMENT_INSPECTION_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Inspection option',
    valueHint: 'Value',
    appChoiceKey: '21st Improvement Inspection Options'
  },
  [COUNTRY_PLACE_FHA_PA_IMPROVEMENTS_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Improvement',
    valueHint: 'Value',
    appChoiceKey: 'Country Place (FHA) PA Improvements',
    defaultValue: 'Inc'
  },
  [TINY_HOME_ADDITIONAL_PRODUCTS_FIELD]: {
    shape: 'fixedValueList',
    textHint: 'Value / Inc',
    appChoiceKey: 'Tiny Home Additional Products',
    defaultValue: 'Inc'
  },
  [COMMISSION_SHEET_OPTIONS_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Commission option',
    valueHint: 'Amount',
    label: 'Commission options',
    appChoiceKey: 'Commission Options',
    /** App choice values are { salesLocation, category, values: [{name}] } */
    nestedCommissionAppChoice: true
  },
  [COMMISSION_SHEET_LESS_OPTIONS_FIELD]: {
    shape: 'keyValue',
    keyHint: 'Less option',
    valueHint: 'Amount',
    label: 'Less options',
    appChoiceKey: 'Commission Less Options',
    nestedCommissionAppChoice: true
  }
};

const buildOptionalEquipmentSlots = () => {
  /**
   * New_Sales_Agreement: rows 21–30
   * key@B (merge B–D), value@E (merge E–F) — Apps Script [key,"","",value] at (21,2)
   */
  const newSalesAgreement = {
    match: 'exact',
    sheetName: 'new_sales_agreement',
    field: OPTIONAL_EQUIPMENT_FIELD,
    shape: 'keyValue',
    maxRows: 10,
    slots: Array.from({ length: 10 }, (_, i) => ({
      index: i,
      row: 21 + i,
      keyCol: 2,
      valueCol: 5
    }))
  };

  /**
   * Sales Agreement: rows 28–32
   * first 5 → key@B value@C ; last 5 → key@D value@F
   */
  const salesAgreement = {
    match: 'include',
    sheetName: 'sales agreement',
    field: OPTIONAL_EQUIPMENT_FIELD,
    shape: 'keyValue',
    maxRows: 10,
    slots: [
      ...Array.from({ length: 5 }, (_, i) => ({
        index: i,
        row: 28 + i,
        keyCol: 2,
        valueCol: 3
      })),
      ...Array.from({ length: 5 }, (_, i) => ({
        index: 5 + i,
        row: 28 + i,
        keyCol: 4,
        valueCol: 6
      }))
    ]
  };

  return [newSalesAgreement, salesAgreement];
};

const buildRetailerOptionsSlots = () => [
  {
    match: 'include',
    sheetName: 'sales agreement',
    field: RETAILER_OPTIONS_FIELD,
    shape: 'keyValue',
    maxRows: 5,
    slots: Array.from({ length: 5 }, (_, i) => ({
      index: i,
      row: 34 + i,
      keyCol: 2,
      valueCol: 6
    }))
  }
];

const buildNewSalesAgreementStringListSlots = () => [
  /**
   * sellerPaidOptions: rows 34–37, max 4, single text @B (merge B–F)
   * Apps Script: map(option => [option]) at (34, 2)
   */
  {
    match: 'exact',
    sheetName: 'new_sales_agreement',
    field: SELLER_PAID_OPTIONS_FIELD,
    shape: 'stringList',
    maxRows: 4,
    slots: Array.from({ length: 4 }, (_, i) => ({
      index: i,
      row: 34 + i,
      textCol: 2
    }))
  },
  /**
   * promiseListItems: rows 39–45, max 7, single text @B (merge B–F)
   * Apps Script: map(item => [item]) at (39, 2)
   */
  {
    match: 'exact',
    sheetName: 'new_sales_agreement',
    field: PROMISE_LIST_ITEMS_FIELD,
    shape: 'stringList',
    maxRows: 7,
    slots: Array.from({ length: 7 }, (_, i) => ({
      index: i,
      row: 39 + i,
      textCol: 2
    }))
  }
];

/**
 * 21st Improvement Funds INVOICE — dynamic rows.
 * Apps Script: insertRows(12, N-1) then write [key,"",value] at (12,2)
 * → key@B value@D, template has 1 data row before TOTAL.
 */
const buildTwentyfirstImprovementFundsSlots = () => [
  {
    match: 'include',
    sheetName: '21st improvement funds invoice',
    field: TWENTYFIRST_IMPROVEMENT_FUNDS_FIELD,
    shape: 'keyValue',
    expandable: true,
    startRow: 12,
    keyCol: 2,
    valueCol: 4,
    minRows: 1,
    maxRows: 40,
    slots: [] // built dynamically from documentState
  }
];

/**
 * 21st site improvement inspection — key/value list.
 * Apps Script: includes("21st site improvement inspection")
 * write [key, "", "", "", "", "", "", "", value] at (10, 4)
 * → key@D value@L. No insertRows on print; editor still expands.
 */
const buildTwentifirstImprovementInspectionSlots = () => [
  {
    match: 'include',
    sheetName: '21st site improvement inspection',
    field: TWENTIFIRST_IMPROVEMENT_INSPECTION_FIELD,
    shape: 'keyValue',
    expandable: true,
    startRow: 10,
    keyCol: 4,
    valueCol: 12,
    minRows: 1,
    maxRows: 40,
    slots: []
  }
];

/**
 * Country Place (FHA) PA — improvements key/value list.
 * Apps Script: includes("country place (fha) pa")
 * write [key, "", "", value] at (37, 2) → key@B value@E.
 * No insertRows on print; editor still expands like inspection.
 */
const buildCountryPlaceFhaPaImprovementsSlots = () => [
  {
    match: 'include',
    sheetName: 'country place (fha) pa',
    field: COUNTRY_PLACE_FHA_PA_IMPROVEMENTS_FIELD,
    shape: 'keyValue',
    expandable: true,
    startRow: 37,
    keyCol: 2,
    valueCol: 5,
    minRows: 1,
    maxRows: 40,
    slots: []
  }
];

/**
 * Tiny home purchase agreement — fixed product keys from appChoices.
 * Apps Script: includes("tiny home purchase agreement")
 * write options.map(a => [a]) at (25, 16) → values @P starting row 25.
 * CRM stores string[] parallel to fixed keys ('' = unchecked, often 'Inc').
 */
const buildTinyHomeAdditionalProductsSlots = () => [
  {
    match: 'include',
    sheetName: 'tiny home purchase agreement',
    field: TINY_HOME_ADDITIONAL_PRODUCTS_FIELD,
    shape: 'fixedValueList',
    fixedKeysFromAppChoice: true,
    startRow: 25,
    textCol: 16,
    slots: []
  }
];

/**
 * Commission Sheet — two expandable key/value tables on the same sheet.
 * Apps Script: includes("commission sheet")
 * - Less: insertRowsAfter(17, N-1); write ["Less", key, "", value] at (17, 3)
 *   → key@D value@F (C stays "Less")
 * - Options: insertRowsAfter(22, N-1); write [index+2, key, "", "", value] at (22, 2)
 *   → key@C value@F (B is print-time index)
 */
const buildCommissionSheetSlots = () => [
  {
    match: 'include',
    sheetName: 'commission sheet',
    field: COMMISSION_SHEET_LESS_OPTIONS_FIELD,
    shape: 'keyValue',
    expandable: true,
    startRow: 17,
    keyCol: 4,
    valueCol: 6,
    minRows: 1,
    maxRows: 40,
    slots: []
  },
  {
    match: 'include',
    sheetName: 'commission sheet',
    field: COMMISSION_SHEET_OPTIONS_FIELD,
    shape: 'keyValue',
    expandable: true,
    startRow: 22,
    keyCol: 3,
    valueCol: 6,
    minRows: 1,
    maxRows: 40,
    slots: []
  }
];

export const CUSTOM_SHEET_FIELD_LAYOUTS = [
  ...buildOptionalEquipmentSlots(),
  ...buildRetailerOptionsSlots(),
  ...buildNewSalesAgreementStringListSlots(),
  ...buildTwentyfirstImprovementFundsSlots(),
  ...buildTwentifirstImprovementInspectionSlots(),
  ...buildCountryPlaceFhaPaImprovementsSlots(),
  ...buildTinyHomeAdditionalProductsSlots(),
  ...buildCommissionSheetSlots()
];

export const resolveCustomSheetLayouts = sheetName => {
  /**
   * Mirror Apps Script sheet matching:
   * - most handlers use name.includes(needle)
   * - new_sales_agreement print block uses name === 'new_sales_agreement'
   * - sales agreement is an else-if after that exact check
   */
  const name = String(sheetName || '').toLowerCase();
  if (!name) return [];

  const isNewSalesAgreement = name === 'new_sales_agreement';

  const exact = CUSTOM_SHEET_FIELD_LAYOUTS.filter(
    layout => layout.match === 'exact' && name === layout.sheetName
  );
  const exactFields = new Set(exact.map(l => l.field));

  const include = CUSTOM_SHEET_FIELD_LAYOUTS.filter(layout => {
    if (layout.match !== 'include') return false;
    if (!name.includes(layout.sheetName)) return false;
    if (exactFields.has(layout.field)) return false;
    // Apps Script: new_sales_agreement uses === branch; sales agreement is else-if
    if (isNewSalesAgreement && layout.sheetName === 'sales agreement') return false;
    return true;
  });

  return [...exact, ...include];
};

/**
 * When multiple expandable tables share a sheet, rows inserted above shift
 * everything below. Return layouts with effective startRow for binding/slots.
 * expandSheetForCustomTables inserts bottom-to-top so visuals stay aligned.
 */
export const resolveCustomSheetLayoutsWithRowShift = (sheetName, documentState) => {
  const layouts = resolveCustomSheetLayouts(sheetName);
  if (layouts.length <= 1) return layouts;

  const ordered = [...layouts].sort((a, b) => a.startRow - b.startRow);
  let shift = 0;
  const shiftedByField = new Map();

  ordered.forEach(layout => {
    shiftedByField.set(layout.field, {
      ...layout,
      startRow: layout.startRow + shift,
      templateStartRow: layout.startRow
    });
    if (layout.expandable) {
      const visible = getExpandableVisibleRowCount(documentState, layout);
      shift += Math.max(0, visible - 1);
    }
  });

  // Preserve resolveCustomSheetLayouts order for stable UI action bars.
  return layouts.map(layout => shiftedByField.get(layout.field) || layout);
};

export const normalizeKeyValueTable = value => {
  if (!Array.isArray(value)) return [];
  return value.map(entry => {
    if (Array.isArray(entry)) {
      return { key: entry[0] ?? '', value: entry[1] ?? '' };
    }
    if (entry && typeof entry === 'object') {
      return { key: entry.key ?? '', value: entry.value ?? '' };
    }
    return { key: entry != null ? String(entry) : '', value: '' };
  });
};

/** CRM stores string[]; form may use [[text]] tuples or { key }. */
export const normalizeStringList = value => {
  if (!Array.isArray(value)) return [];
  return value.map(entry => {
    if (entry == null) return '';
    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
    if (Array.isArray(entry)) return entry[0] != null ? String(entry[0]) : '';
    if (typeof entry === 'object') {
      if (entry.key != null) return String(entry.key);
      if (entry.value != null && entry.key == null) return String(entry.value);
    }
    return '';
  });
};

/** Visible editor rows for expandable tables (at least minRows). */
export const getExpandableVisibleRowCount = (documentState, layout) => {
  const minRows = layout.minRows ?? 1;
  const maxRows = layout.maxRows ?? 40;
  const stored = normalizeKeyValueTable(documentState?.[layout.field]).length;
  return Math.min(maxRows, Math.max(minRows, stored));
};

export const getFixedValueListKeys = (appChoices, field) => {
  const appChoiceKey = FIELD_META[field]?.appChoiceKey;
  if (!appChoiceKey) return [];
  const choice = (appChoices || []).find(a => a.key === appChoiceKey);
  return (choice?.values || [])
    .map(v => (typeof v === 'object' ? v.name || v.key : v))
    .filter(Boolean)
    .map(String);
};

export const buildLayoutSlots = (layout, documentState, appChoices) => {
  if (layout.fixedKeysFromAppChoice || layout.shape === 'fixedValueList') {
    const keys = getFixedValueListKeys(appChoices, layout.field);
    return keys.map((keyLabel, i) => ({
      index: i,
      row: layout.startRow + i,
      textCol: layout.textCol,
      keyLabel
    }));
  }

  if (!layout.expandable) return layout.slots || [];

  const count = getExpandableVisibleRowCount(documentState, layout);
  return Array.from({ length: count }, (_, i) => ({
    index: i,
    row: layout.startRow + i,
    keyCol: layout.keyCol,
    valueCol: layout.valueCol
  }));
};

/**
 * @returns {{
 *   field: string,
 *   shape: 'keyValue'|'stringList'|'fixedValueList',
 *   index: number,
 *   prop: 'key'|'value'|'text',
 *   inputType: string,
 *   hint: string,
 *   expandable?: boolean
 * } | null}
 */
export const getCustomCellBinding = (sheetName, row1, col1, documentState, appChoices) => {
  const layouts = resolveCustomSheetLayoutsWithRowShift(sheetName, documentState);
  const metaFor = field => FIELD_META[field] || {};

  for (const layout of layouts) {
    const slots = buildLayoutSlots(layout, documentState, appChoices);
    for (const slot of slots) {
      if (slot.row !== row1) continue;

      if (slot.textCol === col1) {
        const meta = metaFor(layout.field);
        const isFixed = layout.shape === 'fixedValueList' || FIXED_VALUE_LIST_FIELDS.has(layout.field);
        return {
          field: layout.field,
          shape: isFixed ? 'fixedValueList' : 'stringList',
          index: slot.index,
          prop: 'text',
          inputType: 'text',
          hint: slot.keyLabel || meta.textHint || 'Item',
          expandable: Boolean(layout.expandable)
        };
      }

      if (slot.keyCol === col1) {
        const meta = metaFor(layout.field);
        return {
          field: layout.field,
          shape: 'keyValue',
          index: slot.index,
          prop: 'key',
          inputType: 'text',
          hint: meta.keyHint || 'Option',
          expandable: Boolean(layout.expandable)
        };
      }

      if (slot.valueCol === col1) {
        const meta = metaFor(layout.field);
        return {
          field: layout.field,
          shape: 'keyValue',
          index: slot.index,
          prop: 'value',
          inputType: 'number',
          hint: meta.valueHint || 'Value',
          expandable: Boolean(layout.expandable)
        };
      }
    }
  }
  return null;
};

export const getCustomTableCellValue = (documentState, binding) => {
  if (!binding) return '';
  if (binding.shape === 'stringList' || binding.shape === 'fixedValueList') {
    const list = normalizeStringList(documentState?.[binding.field]);
    return list[binding.index] ?? '';
  }
  const rows = normalizeKeyValueTable(documentState?.[binding.field]);
  const row = rows[binding.index] || { key: '', value: '' };
  return row[binding.prop] ?? '';
};

export const compactKeyValueTable = value =>
  normalizeKeyValueTable(value).filter(
    row => String(row.key ?? '').trim() !== '' || String(row.value ?? '').trim() !== ''
  );

/**
 * Mirror the create/edit form (`convertFormToKeyValueObject`): a row that has a
 * key but no supplied value falls back to the field's default (e.g. 'Inc').
 * No-op for fields without a meaningful default (undefined/null/'').
 */
export const applyKeyValueDefaultValue = (rows, field) => {
  const defaultValue = FIELD_META[field]?.defaultValue;
  if (defaultValue === undefined || defaultValue === null || defaultValue === '') {
    return rows;
  }
  return rows.map(row => {
    const hasKey = String(row.key ?? '').trim() !== '';
    const hasValue = String(row.value ?? '').trim() !== '';
    return hasKey && !hasValue ? { ...row, value: defaultValue } : row;
  });
};

export const compactStringList = value =>
  normalizeStringList(value).filter(item => String(item).trim() !== '');

export const compactCustomTableField = (field, value) => {
  if (STRING_LIST_TABLE_FIELDS.has(field)) return compactStringList(value);
  if (KEY_VALUE_TABLE_FIELDS.has(field)) return compactKeyValueTable(value);
  return value;
};

/** Drop trailing blank entries so cleared last rows leave state immediately. */
const trimTrailingEmptyStringList = list => {
  const next = [...list];
  while (next.length > 0 && String(next[next.length - 1]).trim() === '') {
    next.pop();
  }
  return next;
};

const trimTrailingEmptyKeyValueRows = rows => {
  const next = [...rows];
  while (
    next.length > 0 &&
    String(next[next.length - 1]?.key ?? '').trim() === '' &&
    String(next[next.length - 1]?.value ?? '').trim() === ''
  ) {
    next.pop();
  }
  return next;
};

export const updateKeyValueTableEntry = (documentState, field, index, prop, cellValue) => {
  const rows = normalizeKeyValueTable(documentState?.[field]);
  const nextRows = [...rows];
  while (nextRows.length <= index) {
    nextRows.push({ key: '', value: '' });
  }
  nextRows[index] = {
    ...nextRows[index],
    [prop]: cellValue
  };

  // Expandable tables keep blank trailing rows until save/print snapshot.
  const shouldKeepTrailing = CUSTOM_SHEET_FIELD_LAYOUTS.some(
    layout => layout.field === field && layout.expandable
  );

  return {
    ...documentState,
    [field]: shouldKeepTrailing ? nextRows : trimTrailingEmptyKeyValueRows(nextRows)
  };
};

export const updateStringListEntry = (documentState, field, index, cellValue) => {
  const list = normalizeStringList(documentState?.[field]);
  const next = [...list];
  while (next.length <= index) {
    next.push('');
  }
  next[index] = cellValue;

  // Fixed-key lists must keep positional empties ('' = unchecked).
  if (FIXED_VALUE_LIST_FIELDS.has(field)) {
    return { ...documentState, [field]: next };
  }

  return {
    ...documentState,
    [field]: trimTrailingEmptyStringList(next)
  };
};

export const updateCustomTableEntry = (documentState, field, index, prop, cellValue) => {
  if (
    STRING_LIST_TABLE_FIELDS.has(field) ||
    FIXED_VALUE_LIST_FIELDS.has(field) ||
    prop === 'text'
  ) {
    return updateStringListEntry(documentState, field, index, cellValue);
  }
  return updateKeyValueTableEntry(documentState, field, index, prop, cellValue);
};

export const addExpandableTableRow = (documentState, field, layout) => {
  const maxRows = layout?.maxRows ?? 40;
  const rows = normalizeKeyValueTable(documentState?.[field]);
  const base = rows.length === 0 ? [{ key: '', value: '' }] : [...rows];
  if (base.length >= maxRows) return documentState;
  return {
    ...documentState,
    [field]: [...base, { key: '', value: '' }]
  };
};

export const removeExpandableTableRow = (documentState, field, index) => {
  const rows = normalizeKeyValueTable(documentState?.[field]);
  if (!rows.length) return documentState;
  return {
    ...documentState,
    [field]: rows.filter((_, i) => i !== index)
  };
};

/**
 * Build replacement payloads for custom table fields from registered sheet slots.
 * Always overwrites these keys (including []) so cleared on-screen rows cannot
 * leave ghost values from a longer previous array.
 */
export const snapshotCustomTablesForDocument = (documentState, documentLayout, appChoices) => {
  const layoutsByField = new Map();

  (documentLayout?.sheets || []).forEach(sheet => {
    resolveCustomSheetLayouts(sheet.name).forEach(layout => {
      if (!layoutsByField.has(layout.field)) {
        layoutsByField.set(layout.field, layout);
      }
    });
  });

  const result = {};

  layoutsByField.forEach((layout, field) => {
    if (layout.expandable) {
      result[field] = applyKeyValueDefaultValue(
        compactKeyValueTable(normalizeKeyValueTable(documentState?.[field])),
        field
      );
      return;
    }

    if (layout.shape === 'fixedValueList' || FIXED_VALUE_LIST_FIELDS.has(field)) {
      const keys = getFixedValueListKeys(appChoices, field);
      const list = normalizeStringList(documentState?.[field]);
      // Keep positional empties so print/CRM stay aligned with fixed keys.
      result[field] = keys.length
        ? keys.map((_, i) => list[i] ?? '')
        : list;
      return;
    }

    const maxRows = layout.maxRows || layout.slots?.length || 0;
    if (layout.shape === 'stringList' || STRING_LIST_TABLE_FIELDS.has(field)) {
      const list = normalizeStringList(documentState?.[field]);
      const fromScreen = Array.from({ length: maxRows }, (_, i) => list[i] ?? '');
      result[field] = compactStringList(fromScreen);
      return;
    }

    const rows = normalizeKeyValueTable(documentState?.[field]);
    const fromScreen = Array.from({ length: maxRows }, (_, i) => rows[i] || { key: '', value: '' });
    result[field] = applyKeyValueDefaultValue(compactKeyValueTable(fromScreen), field);
  });

  return result;
};

export const normalizeCustomTableField = (field, value) => {
  if (STRING_LIST_TABLE_FIELDS.has(field) || FIXED_VALUE_LIST_FIELDS.has(field)) {
    return normalizeStringList(value);
  }
  if (KEY_VALUE_TABLE_FIELDS.has(field)) return normalizeKeyValueTable(value);
  return value;
};

export const getCustomTableFieldLabel = field =>
  FIELD_META[field]?.label || FIELD_META[field]?.keyHint || field;

export const getTableFieldDatalistOptions = (appChoices, field) => {
  const meta = FIELD_META[field];
  const appChoiceKey = meta?.appChoiceKey;
  if (!appChoiceKey) return [];
  const choice = (appChoices || []).find(a => a.key === appChoiceKey);
  const values = choice?.values || [];

  if (meta.nestedCommissionAppChoice) {
    const names = [];
    const seen = new Set();
    values.forEach(entry => {
      (entry?.values || []).forEach(opt => {
        const name = typeof opt === 'object' ? opt.name || opt.key : opt;
        if (!name || seen.has(String(name))) return;
        seen.add(String(name));
        names.push(String(name));
      });
    });
    return names;
  }

  return values
    .map(v => (typeof v === 'object' ? v.name || v.key : v))
    .filter(Boolean)
    .map(String);
};

/** Seed documentState commission tables from the linked inventory unit. */
export const pickCommissionTablesFromInventory = inventoryUnit => {
  if (!inventoryUnit) {
    return {
      [COMMISSION_SHEET_OPTIONS_FIELD]: [],
      [COMMISSION_SHEET_LESS_OPTIONS_FIELD]: []
    };
  }
  return {
    [COMMISSION_SHEET_OPTIONS_FIELD]: normalizeKeyValueTable(
      inventoryUnit.commissionSheetOptions
    ),
    [COMMISSION_SHEET_LESS_OPTIONS_FIELD]: normalizeKeyValueTable(
      inventoryUnit.commissionSheetLessOptions
    )
  };
};

/** Compact commission tables for inventory PUT / print documentParams. */
export const snapshotCommissionTablesForInventory = documentState => ({
  [COMMISSION_SHEET_OPTIONS_FIELD]: compactKeyValueTable(
    normalizeKeyValueTable(documentState?.[COMMISSION_SHEET_OPTIONS_FIELD])
  ),
  [COMMISSION_SHEET_LESS_OPTIONS_FIELD]: compactKeyValueTable(
    normalizeKeyValueTable(documentState?.[COMMISSION_SHEET_LESS_OPTIONS_FIELD])
  )
});

export const documentLayoutHasCommissionTables = documentLayout =>
  (documentLayout?.sheets || []).some(
    sheet =>
      resolveCustomSheetLayouts(sheet.name).some(layout =>
        INVENTORY_COMMISSION_TABLE_FIELDS.has(layout.field)
      )
  );

/** @deprecated use getTableFieldDatalistOptions */
export const getOptionalEquipmentDatalistOptions = appChoices =>
  getTableFieldDatalistOptions(appChoices, OPTIONAL_EQUIPMENT_FIELD);
